import { Flex, HStack, Tag, TagLabel, Text } from '@chakra-ui/react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CustomFieldValue } from '../../../types';
import { COVERAGE_LABELS, COVERAGE_TYPE } from './coverageTypes';

type CrossCoverageSickBadgeProps = {
  customField?: CustomFieldValue[];
  scope?: 'clinic' | 'market';
};

const CrossCoverageSickBadge: React.FC<
  CrossCoverageSickBadgeProps
> = ({ customField, scope = 'clinic' }) => {
  const anyoneLabel =
    scope === 'market' ? 'Anyone in Market' : 'Anyone in Clinic';
  const navigate = useNavigate();

  // First: Early exit if empty
  if (!Array.isArray(customField) || customField.length === 0) {
    return null;
  }

  const field =
    customField.find((r) => {
      const v = typeof r.value === 'string' ? r.value.trim() : '';
      const ct = r.metadata?.coverageType as string | undefined;
      return (
        (v !== '' && v !== 'None') ||
        Boolean(ct && COVERAGE_LABELS[ct]) ||
        (Array.isArray(r.metadata?.providers) && (r.metadata.providers as unknown[]).length > 0)
      );
    }) || customField[0];

  const rawValue = typeof field.value === 'string' ? field.value.trim() : '';
  const coverageType = field.metadata?.coverageType as string | undefined;
  const rawProviders = field.metadata?.providers as
    | { providerId?: string; id?: string; providerLabel?: string; label?: string }[]
    | undefined;
  const providers = Array.isArray(rawProviders)
    ? rawProviders
        .map((p) => ({
          providerId: p.providerId || p.id || '',
          providerLabel: p.providerLabel || p.label || '',
        }))
        .filter((p) => p.providerId)
    : undefined;

  const labelForCoverageType =
    coverageType === COVERAGE_TYPE.ANYONE_MARKET
      ? anyoneLabel
      : coverageType && COVERAGE_LABELS[coverageType]
      ? COVERAGE_LABELS[coverageType]
      : null;

  const normalizedRawValue =
    rawValue === 'Anyone in Market' ? anyoneLabel : rawValue;

  const coverageDetails =
    labelForCoverageType !== null
      ? labelForCoverageType
      : normalizedRawValue !== '' && normalizedRawValue !== 'None'
      ? normalizedRawValue
      : Array.isArray(providers) && providers.length > 0
      ? 'Preferred Provider'
      : '';

  // If there is no displayable content, render nothing — empty state is intentionally blank
  if (!coverageDetails && (!providers || providers.length === 0)) {
    return null;
  }

  // Show coverage details based on type
  return (
    <Flex direction="column" gap="0.25rem">
      {/* Main coverage value styled like a compact badge label */}
      <Text
        fontSize="0.875rem"
        fontWeight="700"
        color="text.primary"
        lineHeight="1.25rem"
        fontStyle={
          coverageDetails === 'Free Text' ? 'italic' : 'normal'
        }
      >
        {coverageDetails}
      </Text>

      {/* Handle specific coverage types */}
      {coverageDetails === 'Preferred Provider' &&
        providers &&
        providers.length > 0 && (
          <HStack spacing={1} flexWrap="wrap" mt={1}>
            {providers.map((p) => (
              <Tag
                as="a"
                key={p.providerId}
                size="sm"
                colorScheme="blue"
                borderRadius="full"
                variant="outline"
                cursor="pointer"
                href={`/providers/${p.providerId}`}
                target="_blank"
                rel="noopener noreferrer"
                _hover={{
                  bg: 'blue.50',
                  borderColor: 'blue.400',
                  transform: 'translateY(-1px)',
                }}
                _active={{ transform: 'translateY(0)' }}
                transition="all 0.15s ease-in-out"
                fontSize="0.75rem"
                fontWeight="500"
              >
                <TagLabel>{p.providerLabel}</TagLabel>
              </Tag>
            ))}
          </HStack>
        )}

      {/* Free Text instructions */}
      {coverageDetails === 'Free Text' &&
        field.metadata?.freeTextInstructions && (
          <Text
            fontSize="0.75rem"
            color="gray.600"
            fontStyle="italic"
            mt={1}
          >
            {String(field.metadata.freeTextInstructions)}
          </Text>
        )}

      {/* Free Text with providers */}
      {coverageDetails === 'Free Text' &&
        providers &&
        providers.length > 0 && (
          <>
            <Text
              fontSize="0.75rem"
              color="orange.600"
              fontStyle="italic"
            >
              With specific providers:
            </Text>
            <HStack spacing={1} flexWrap="wrap">
              {providers.map((p) => (
                <Tag
                  key={p.providerId}
                  size="sm"
                  colorScheme="blue"
                  borderRadius="full"
                  variant="outline"
                  cursor="pointer"
                  onClick={() =>
                    navigate(`/providers/${p.providerId}`)
                  }
                  _hover={{ bg: 'blue.50', borderColor: 'blue.400' }}
                  transition="all 0.15s ease-in-out"
                  fontSize="0.75rem"
                  fontWeight="500"
                >
                  <TagLabel>{p.providerLabel}</TagLabel>
                </Tag>
              ))}
            </HStack>
          </>
        )}

      {/* Omit extra who/what line to match Medicare Wellness visual simplicity */}
    </Flex>
  );
};

export default CrossCoverageSickBadge;
